/**
 * Cognara 4D Linkage — HITL review console.
 *
 * Full-screen workspace: the live 3D viewer stays mounted above (the page keeps
 * one viewer instance), this console owns the review below. Clicking an
 * activity paints its proposed objects in the viewer; the dry-run timeliner
 * scrubs proposed links without writing anything.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { authFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Sparkles, Check, X, RotateCcw, Play, Pause, SkipBack, SkipForward,
  Loader2, Crosshair, GitCommitHorizontal, Trash2, Info, Layers, Zap, Pencil, CheckCheck,
} from 'lucide-react';
import {
  ResizablePanelGroup, ResizablePanel, ResizableHandle,
} from '@/components/ui/resizable';
import { WbsProposalTree } from './WbsProposalTree';
import { ObjectScopeEditor } from './ObjectScopeEditor';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const BAND_STYLE = {
  HIGH: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  MEDIUM: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  LOW: 'bg-rose-500/15 text-rose-600 border-rose-500/30',
};
const STATUS_STYLE = {
  PENDING: 'bg-slate-500/15 text-slate-500 border-slate-500/30',
  ACCEPTED: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  REJECTED: 'bg-rose-500/15 text-rose-600 border-rose-500/30',
  COMMITTED: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
};

export function CognaraLinkHitl({ projectId, modelId, viewerRef, onClose, onCommitted, canEdit }) {
  const base = `${API}/api/v1/projects/${projectId}/4d/models/${modelId}/cognara`;

  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [run, setRun] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [wbs, setWbs] = useState([]);
  const [stepActivities, setStepActivities] = useState([]);
  const [counts, setCounts] = useState({});
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [threshold, setThreshold] = useState([80]);
  const [committing, setCommitting] = useState(false);
  const [commitJob, setCommitJob] = useState(null);
  const [commitAsk, setCommitAsk] = useState(null);   // {simulate} while confirming
  const [acceptAllAsk, setAcceptAllAsk] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);

  // dry-run timeliner
  const [dryRun, setDryRun] = useState(null);
  const [dryIndex, setDryIndex] = useState(0);
  const [dryPlaying, setDryPlaying] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const playTimer = useRef(null);
  const pollTimer = useRef(null);
  const toastRef = useRef(null);
  const commitTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pr, tr] = await Promise.all([
        authFetch(`${base}/proposals`),
        authFetch(`${base}/wbs-tree`),
      ]);
      const d = await pr.json().catch(() => ({}));
      if (pr.ok) { setRun(d.run || null); setProposals(d.proposals || []); setCounts(d.counts || {}); }
      else toast.error(d.detail || 'Could not load proposals');
      const t = await tr.json().catch(() => ({}));
      if (tr.ok) setWbs(t.activities || []);
    } catch { toast.error('Could not load proposals'); }
    setLoading(false);
  }, [base]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => () => {
    if (playTimer.current) clearInterval(playTimer.current);
    if (pollTimer.current) clearInterval(pollTimer.current);
    if (commitTimer.current) clearInterval(commitTimer.current);
    // A loading toast outlives the component unless it is explicitly dismissed,
    // which is how "Cognara is linking activities …" got stuck on screen after
    // the console was closed.
    if (toastRef.current) { toast.dismiss(toastRef.current); toastRef.current = null; }
    viewerRef?.current?.clearHotspot?.();
  }, [viewerRef]);

  const runAnalysis = async (force) => {
    setRunning(true);
    const t = toast.loading(force ? 'Cognara is re-analysing the model and schedule…'
                                  : 'Cognara is reading the 3D model and macro schedule…');
    toastRef.current = t;
    try {
      const r = await authFetch(`${base}/propose`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ force: !!force, scope: 'ALL' }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || 'Analysis failed', { id: t }); toastRef.current = null; setRunning(false); return; }
      if (d.status === 'cached') {
        toast.success('Loaded the previous analysis (no AI spend)', { id: t });
        toastRef.current = null;
        await load(); setRunning(false); return;
      }
      // Background job — poll until it lands. The reasoning pass runs far
      // longer than a single HTTP request is allowed to.
      pollRun(t);
    } catch { toast.error('Analysis failed', { id: t }); toastRef.current = null; setRunning(false); }
  };

  const pollRun = useCallback((toastId) => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    toastRef.current = toastId;
    pollTimer.current = setInterval(async () => {
      try {
        const r = await authFetch(`${base}/run-status`);
        const d = await r.json().catch(() => ({}));
        const rn = d.run;
        if (!rn) return;
        setRun(rn);
        if (rn.status === 'RUNNING') {
          toast.loading(rn.phase || 'Cognara is analysing…', { id: toastId });
          return;
        }
        clearInterval(pollTimer.current); pollTimer.current = null;
        setRunning(false);
        if (rn.status === 'FAILED') {
          toast.error(rn.error || 'Analysis failed', { id: toastId });
        } else {
          toast.success(
            `Analysis complete · ${rn.activitiesMatched ?? 0}/${rn.activitiesAnalysed ?? 0} activities matched in ${rn.durationSeconds ?? 0}s`,
            { id: toastId });
        }
        toastRef.current = null;
        await load();
      } catch { /* keep polling — a transient blip must not kill the run */ }
    }, 4000);
  }, [base, load]);

  // Re-attach to a run that is still in flight (e.g. after a page reload).
  useEffect(() => {
    if (run?.status === 'RUNNING' && !pollTimer.current) {
      setRunning(true);
      pollRun(toast.loading(run.phase || 'Cognara is analysing…'));
    }
  }, [run, pollRun]);

  const selected = useMemo(
    () => proposals.find(p => p.activityId === selectedId) || null,
    [proposals, selectedId]);

  // The tree renders every WBS row; proposal state is merged in so a collapsed
  // package still shows what sits underneath it.
  const wbsWithProposals = useMemo(() => {
    const byId = new Map(proposals.map(p => [p.activityId, p]));
    return (wbs || []).map(a => {
      const p = byId.get(a.activityId);
      return p ? { ...a, proposal: {
        status: p.status, confidence: p.confidence, confidenceBand: p.confidenceBand,
        proposedCount: p.proposedCount, disagreement: p.disagreement, edited: p.edited,
      } } : a;
    });
  }, [wbs, proposals]);

  // Inline build-up: the tree itself shows what has been constructed, so the
  // reviewer reads the sequence against the WBS instead of a detached list.
  const buildupByCode = useMemo(() => {
    if (!dryRun) return null;
    const map = {};
    stepActivities.forEach(a => { map[a.activityCode] = a; });
    return map;
  }, [dryRun, stepActivities]);

  const newThisStep = useMemo(
    () => stepActivities.filter(a => a.isNew),
    [stepActivities]);

  const newCodes = useMemo(
    () => newThisStep.map(a => a.activityCode),
    [newThisStep]);

  // How many pending proposals the current slider position would accept — the
  // number the commit dialog offers to unblock with.
  const eligibleAtThreshold = useMemo(
    () => proposals.filter(p => p.status === 'PENDING'
      && (p.confidence ?? 0) >= threshold[0] / 100).length,
    [proposals, threshold]);

  const paint = useCallback((p) => {
    const ids = (p?.proposedObjects || []).map(o => o.dbId).filter(v => v != null);
    if (!ids.length) { viewerRef?.current?.clearHotspot?.(); return; }
    const color = p.status === 'ACCEPTED' ? '#10B981'
      : p.status === 'REJECTED' ? '#64748B'
      : p.confidenceBand === 'HIGH' ? '#2563EB'
      : p.confidenceBand === 'MEDIUM' ? '#F59E0B' : '#EF4444';
    const ok = viewerRef?.current?.applyHotspot?.(ids.map(dbId => ({ dbId, color })));
    if (ok === false) toast.info('Load the 3D model to preview this linkage.');
  }, [viewerRef]);

  const selectProposal = (p) => {
    setDryPlaying(false);
    setSelectedId(p.activityId);
    paint(p);
  };

  const selectById = useCallback((activityId) => {
    const p = proposals.find(x => x.activityId === activityId);
    if (p) selectProposal(p);
  }, [proposals]);  // eslint-disable-line react-hooks/exhaustive-deps

  const decide = async (p, decision, dbIds) => {
    try {
      const r = await authFetch(`${base}/proposals/${encodeURIComponent(p.activityId)}/decision`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dbIds ? { decision, dbIds } : { decision }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || 'Could not save decision'); return; }
      setProposals(prev => prev.map(x => x.activityId === p.activityId ? d.proposal : x));
      setCounts(prev => {
        const next = { ...prev };
        next[p.status] = Math.max(0, (next[p.status] || 0) - 1);
        next[d.proposal.status] = (next[d.proposal.status] || 0) + 1;
        return next;
      });
      if (d.proposal.activityId === selectedId) paint(d.proposal);
    } catch { toast.error('Could not save decision'); }
  };

  const dropObject = (p, dbId) => {
    const keep = (p.proposedObjects || []).map(o => o.dbId).filter(d => d !== dbId);
    decide(p, p.status === 'PENDING' ? 'RESET' : p.status === 'ACCEPTED' ? 'ACCEPT' : 'REJECT', keep);
  };

  // `all` ignores the confidence slider AND takes the conflicting proposals the
  // threshold path deliberately skips — it is the "I'll review in the model"
  // shortcut, so it must not quietly leave 47 conflicts behind.
  const bulkAccept = async ({ all = false } = {}) => {
    const minConfidence = all ? 0 : threshold[0] / 100;
    try {
      const r = await authFetch(`${base}/proposals/bulk-accept`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ minConfidence, includeDisagreements: all }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || 'Bulk accept failed'); return 0; }
      if (d.accepted) {
        toast.success(all
          ? `All ${d.accepted} pending proposal(s) accepted`
          : `${d.accepted} proposal(s) accepted at ≥ ${threshold[0]}% confidence`);
      } else if (all) {
        toast.info('There is no pending proposal left to accept.');
      } else {
        toast.info(`No pending proposal reaches ${threshold[0]}% confidence — lower the threshold or accept activities individually.`);
      }
      setAcceptAllAsk(false);
      await load();
      return d.accepted || 0;
    } catch { toast.error('Bulk accept failed'); return 0; }
  };

  const runDryTimeliner = async () => {
    setSimulating(true);
    const t = toast.loading('Simulating the proposed 4D sequence…');
    try {
      const r = await authFetch(`${base}/timeliner/dry-run`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: 'PLAN_VS_ACTUAL', include: 'ACCEPTED_AND_PENDING' }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || 'Simulation failed', { id: t }); setSimulating(false); return; }
      setDryRun(d); setDryIndex(0); setSelectedId(null);
      toast.success(`Dry run ready · ${d.totalSnapshots} steps across ${d.activitiesSimulated} activities`, { id: t });
    } catch { toast.error('Simulation failed', { id: t }); }
    setSimulating(false);
  };

  // Steps are fetched on demand — the full object-state set is far too large to
  // ship in one response.
  const paintStep = useCallback(async (sequenceIndex) => {
    try {
      const r = await authFetch(`${base}/timeliner/dry-run/step/${sequenceIndex}`);
      if (!r.ok) return;
      const d = await r.json();
      setStepActivities(d.activities || []);
      const objs = d.visibleObjects || [];
      if (!objs.length) { viewerRef?.current?.clearHotspot?.(); return; }
      viewerRef?.current?.applyHotspot?.(
        objs.map(o => ({ dbId: o.dbId, color: o.colorHex || '#3B82F6' })));
    } catch { /* a dropped frame must not break playback */ }
  }, [base, viewerRef]);

  useEffect(() => {
    if (!dryRun) return;
    const step = dryRun.steps?.[dryIndex];
    if (!step) return;
    paintStep(step.sequenceIndex);
  }, [dryIndex, dryRun, paintStep]);

  useEffect(() => {
    if (playTimer.current) { clearInterval(playTimer.current); playTimer.current = null; }
    if (!dryPlaying || !dryRun) return;
    playTimer.current = setInterval(() => {
      setDryIndex(i => {
        if (i >= (dryRun.steps?.length || 1) - 1) { setDryPlaying(false); return i; }
        return i + 1;
      });
    }, 700);
    return () => { if (playTimer.current) clearInterval(playTimer.current); };
  }, [dryPlaying, dryRun]);

  // Clicking Commit with nothing accepted used to hit a disabled button — no
  // click, no message, no way to know why. Now it opens the dialog and the
  // dialog offers the one action that unblocks it.
  const askCommit = (simulate) => setCommitAsk({ simulate });

  const runCommit = async (simulate) => {
    setCommitAsk(null);
    setCommitting(true);
    try {
      const r = await authFetch(`${base}/commit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: 'REPLACE', regenerateSnapshots: !!simulate }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d.detail || 'Commit failed'); setCommitting(false); return; }
      toast.success(`Committed ${d.activitiesCommitted} activities · ${d.objectsLinked} objects linked`);
      await load();
      onCommitted?.();
      if (d.simulating) pollCommit();
      else setCommitting(false);
    } catch { toast.error('Commit failed'); setCommitting(false); }
  };

  // Rebuilding all five visualisation modes takes minutes, so the server runs
  // it as a job and we follow it here.
  const pollCommit = useCallback(() => {
    const t = toast.loading('Rebuilding the 4D snapshots…');
    toastRef.current = t;
    if (commitTimer.current) clearInterval(commitTimer.current);
    commitTimer.current = setInterval(async () => {
      try {
        const r = await authFetch(`${base}/commit-status`);
        const d = await r.json().catch(() => ({}));
        const job = d.job;
        if (!job) return;
        setCommitJob(job);
        if (job.status === 'RUNNING') {
          toast.loading(job.phase || 'Rebuilding the 4D snapshots…', { id: t });
          return;
        }
        clearInterval(commitTimer.current); commitTimer.current = null;
        setCommitting(false);
        toastRef.current = null;
        if (job.status === 'FAILED') toast.error(job.error || 'Snapshot rebuild failed', { id: t });
        else {
          toast.success(`4D rebuilt · ${job.totalSnapshots} snapshots across ${(job.modes || []).length} modes`, { id: t });
          onCommitted?.();
        }
      } catch { /* transient blips must not abandon the job */ }
    }, 4000);
  }, [base, onCommitted]);

  const snap = dryRun?.steps?.[dryIndex];

  return (
    <div className="flex flex-col h-full bg-background" data-testid="cognara-hitl">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 h-10 border-b shrink-0 bg-gradient-to-r from-orange-500/10 via-transparent to-transparent">
        <Sparkles className="w-4 h-4 text-orange-500" />
        <span className="text-sm font-semibold">Cognara 4D Linkage — Human review</span>
        {run && (
          <span className="text-[11px] text-muted-foreground" data-testid="cognara-run-summary">
            {run.activitiesMatched}/{run.activitiesAnalysed} activities matched · {run.branchesAnalysed} branches read · {run.modelObjects} objects
          </span>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          {commitJob?.status === 'RUNNING' && (
            <Badge variant="outline" className="text-[10px] h-5 border-orange-500/40 text-orange-600"
                   data-testid="cognara-commit-job">
              {commitJob.phase || 'Rebuilding 4D…'}
            </Badge>
          )}
          {['PENDING', 'ACCEPTED', 'REJECTED', 'COMMITTED'].map(s => (counts[s] ? (
            <Badge key={s} variant="outline" className={`text-[10px] h-5 ${STATUS_STYLE[s]}`} data-testid={`cognara-count-${s.toLowerCase()}`}>
              {s.charAt(0) + s.slice(1).toLowerCase()} {counts[s]}
            </Badge>
          ) : null))}
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => runAnalysis(!!run)}
                  disabled={running || !canEdit} data-testid="cognara-run-btn">
            {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            {run ? 'Re-run analysis' : 'Run analysis'}
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onClose} data-testid="cognara-close-btn">
            Close
          </Button>
        </div>
      </div>

      {/* Action bar */}
      <div className="flex items-center gap-2 px-3 h-9 border-b shrink-0 text-xs">
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Filter activities…"
               className="h-7 text-xs max-w-[220px]" data-testid="cognara-search" />
        <div className="flex items-center gap-0.5">
          {['ALL', 'PENDING', 'ACCEPTED', 'REJECTED'].map(s => (
            <Button key={s} size="sm" variant={statusFilter === s ? 'secondary' : 'ghost'}
                    className="h-6 px-2 text-[10px]" onClick={() => setStatusFilter(s)}
                    data-testid={`cognara-filter-${s.toLowerCase()}`}>
              {s.charAt(0) + s.slice(1).toLowerCase()}
            </Button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-2">
          <span className="text-[10px] text-muted-foreground whitespace-nowrap">Auto-accept ≥ {threshold[0]}%</span>
          <Slider value={threshold} onValueChange={setThreshold} min={50} max={100} step={5}
                  className="w-28" data-testid="cognara-threshold" />
          <Button size="sm" variant="outline" className="h-6 px-2 text-[10px]" onClick={() => bulkAccept()}
                  disabled={!proposals.length || !canEdit} data-testid="cognara-bulk-accept">
            Accept above threshold
          </Button>
          <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] gap-1 border-emerald-500/50 text-emerald-600"
                  onClick={() => setAcceptAllAsk(true)}
                  disabled={!counts.PENDING || !canEdit} data-testid="cognara-accept-all">
            <CheckCheck className="w-3 h-3" />
            Accept all ({counts.PENDING || 0})
          </Button>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          {!counts.ACCEPTED && !!proposals.length && (
            <span className="text-[10px] text-amber-600 whitespace-nowrap" data-testid="cognara-commit-hint">
              Commit writes accepted links only · {counts.PENDING || 0} pending, 0 accepted
            </span>
          )}
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={runDryTimeliner}
                  disabled={simulating || !proposals.length} data-testid="cognara-dryrun-btn">
            {simulating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
            Run Timeliner (dry run)
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => askCommit(false)}
                  disabled={committing || !canEdit || !proposals.length} data-testid="cognara-commit-btn">
            {committing ? <Loader2 className="w-3 h-3 animate-spin" /> : <GitCommitHorizontal className="w-3 h-3" />}
            Commit 4D ({counts.ACCEPTED || 0})
          </Button>
          <Button size="sm" className="h-7 text-xs gap-1 bg-orange-600 hover:bg-orange-700"
                  onClick={() => askCommit(true)}
                  disabled={committing || !canEdit || !proposals.length} data-testid="cognara-commit-simulate-btn">
            {committing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
            Commit &amp; Simulate
          </Button>
        </div>
      </div>

      {/* Dry-run scrubber */}
      {dryRun && (
        <div className="flex items-center gap-3 px-3 h-11 border-b shrink-0 bg-blue-500/5" data-testid="cognara-dryrun-bar">
          <Badge variant="outline" className="text-[10px] h-5 border-blue-500/40 text-blue-600">DRY RUN — nothing committed</Badge>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setDryIndex(0)}><SkipBack className="w-3 h-3" /></Button>
          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setDryPlaying(p => !p)} data-testid="cognara-dryrun-play">
            {dryPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
          </Button>
          <Button size="icon" variant="ghost" className="h-6 w-6"
                  onClick={() => setDryIndex(Math.max(0, (dryRun.steps?.length || 1) - 1))}><SkipForward className="w-3 h-3" /></Button>
          <span className="text-xs font-medium w-36 shrink-0" data-testid="cognara-dryrun-step">{snap?.periodLabel || '—'}</span>
          <Slider value={[dryIndex]} min={0} max={Math.max(0, (dryRun.steps?.length || 1) - 1)} step={1}
                  onValueChange={v => { setDryPlaying(false); setDryIndex(v[0]); }} className="flex-1" />
          <span className="text-[10px] text-muted-foreground whitespace-nowrap">
            Planned {snap?.metrics?.plannedProgressPct ?? 0}% · Actual {snap?.metrics?.actualProgressPct ?? 0}% · {snap?.metrics?.objectsVisible ?? 0} objects on
          </span>
          <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] gap-1"
                  onClick={() => { setDryRun(null); setDryPlaying(false); viewerRef?.current?.clearHotspot?.(); }}
                  data-testid="cognara-dryrun-exit">
            <Trash2 className="w-3 h-3" /> Exit dry run
          </Button>
        </div>
      )}

      {/* Body */}
      <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-0">
        {/* WBS tree */}
        <ResizablePanel defaultSize={42} minSize={22} className="flex flex-col min-h-0">
          <ScrollArea className="flex-1">
            {loading ? (
              <div className="p-6 text-center text-xs text-muted-foreground">Loading…</div>
            ) : !proposals.length ? (
              <div className="p-8 text-center space-y-3" data-testid="cognara-empty">
                <Sparkles className="w-6 h-6 mx-auto text-orange-500" />
                <p className="text-xs text-muted-foreground max-w-xs mx-auto">
                  Cognara has not analysed this model yet. It reads the macro-schedule activities and the
                  3D object tree with its properties, then proposes a linkage for every activity for you to review.
                </p>
                <Button size="sm" className="h-7 text-xs gap-1" onClick={() => runAnalysis(false)}
                        disabled={running || !canEdit} data-testid="cognara-run-empty-btn">
                  {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Run analysis
                </Button>
              </div>
            ) : (
              <WbsProposalTree
                activities={wbsWithProposals}
                selectedId={selectedId}
                onSelect={selectById}
                search={search}
                statusFilter={statusFilter}
                buildup={buildupByCode}
                focusCodes={newCodes}
              />
            )}
          </ScrollArea>
        </ResizablePanel>

        <ResizableHandle withHandle data-testid="cognara-split-handle" />

        {/* Detail / build-up */}
        <ResizablePanel defaultSize={58} minSize={25} className="flex flex-col min-h-0">
          {dryRun ? (
            <div className="flex-1 flex flex-col min-h-0" data-testid="cognara-buildup-panel">
              <div className="px-3 py-2 border-b shrink-0 flex items-center gap-2">
                <Layers className="w-4 h-4 text-blue-500" />
                <div>
                  <div className="text-sm font-medium">Schedule build-up — {snap?.periodLabel || '—'}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {stepActivities.length} activit{stepActivities.length === 1 ? 'y' : 'ies'} constructed so far
                    {snap?.newActivities ? ` · ${snap.newActivities} starting this step` : ''}
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-1.5">
                  <Badge variant="outline" className="text-[10px] h-5">{snap?.visibleCount ?? 0} objects on</Badge>
                  <Badge variant="outline" className="text-[10px] h-5 border-blue-500/40 text-blue-600">
                    Step {dryIndex + 1}/{dryRun.steps?.length || 0}
                  </Badge>
                </div>
              </div>
              <ScrollArea className="flex-1">
                {!newThisStep.length ? (
                  <div className="p-6 text-center text-xs text-muted-foreground">
                    {stepActivities.length
                      ? 'Nothing new starts this step — the tree on the left shows everything built so far.'
                      : 'Nothing has started yet at this date — step forward.'}
                  </div>
                ) : (
                  <div className="divide-y">
                    {newThisStep.map(a => (
                      <button key={a.activityCode}
                              onClick={() => { const row = wbsWithProposals.find(x => x.activityCode === a.activityCode); if (row) selectById(row.activityId); }}
                              className="w-full text-left flex items-center gap-2 px-3 py-2 bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors"
                              data-testid={`cognara-buildup-${a.activityCode}`}>
                        <span className="text-[10px] font-mono text-muted-foreground w-20 shrink-0">{a.activityCode}</span>
                        <span className="text-xs truncate">{a.activityName}</span>
                        {a.isSummary && (
                          <Badge variant="outline" className="text-[9px] h-4 px-1 text-muted-foreground shrink-0">
                            Package
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-[9px] h-4 px-1 border-emerald-500/40 text-emerald-600 shrink-0">
                          Starting now
                        </Badge>
                        <span className="ml-auto text-[10px] text-muted-foreground shrink-0">{a.objectCount} obj</span>
                      </button>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          ) : !selected ? (
            <div className="flex-1 flex items-center justify-center text-xs text-muted-foreground gap-2">
              <Crosshair className="w-4 h-4" />
              Select an activity to preview its proposed 3D linkage
            </div>
          ) : (
            <>
              <div className="px-3 py-2 border-b shrink-0">
                <div className="flex items-start gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate" data-testid="cognara-detail-name">{selected.activityName}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">
                      {selected.activityCode} · {String(selected.plannedStart || '').slice(0, 10)} → {String(selected.plannedFinish || '').slice(0, 10)}
                    </div>
                  </div>
                  <div className="ml-auto flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] gap-1"
                            onClick={() => paint(selected)} data-testid="cognara-highlight-btn">
                      <Crosshair className="w-3 h-3" /> Highlight
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] gap-1"
                            onClick={() => setEditorOpen(true)} disabled={!canEdit}
                            data-testid="cognara-edit-objects-btn">
                      <Pencil className="w-3 h-3" /> Edit objects
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] gap-1 text-emerald-600 border-emerald-500/40"
                            onClick={() => decide(selected, 'ACCEPT')} disabled={!canEdit || !selected.proposedCount}
                            data-testid="cognara-accept-btn">
                      <Check className="w-3 h-3" /> Accept
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] gap-1 text-rose-600 border-rose-500/40"
                            onClick={() => decide(selected, 'REJECT')} disabled={!canEdit}
                            data-testid="cognara-reject-btn">
                      <X className="w-3 h-3" /> Reject
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px] gap-1"
                            onClick={() => decide(selected, 'RESET')} disabled={!canEdit}
                            data-testid="cognara-reset-btn">
                      <RotateCcw className="w-3 h-3" /> Reset
                    </Button>
                  </div>
                </div>
              </div>

              <ScrollArea className="flex-1">
                <div className="p-3 space-y-3">
                  <div className="rounded-md border p-2.5 bg-muted/30">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-orange-500" /> Cognara reasoning
                    </div>
                    <p className="text-xs leading-relaxed" data-testid="cognara-rationale">{selected.rationale || '—'}</p>
                    {!!(selected.evidence || []).length && (
                      <ul className="mt-2 space-y-0.5">
                        {selected.evidence.map((e, i) => (
                          <li key={i} className="text-[11px] text-muted-foreground flex gap-1.5">
                            <Info className="w-3 h-3 mt-0.5 shrink-0" /><span>{e}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="rounded-md border p-2.5">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1.5">Selection rule</div>
                    <div className="flex flex-wrap gap-1 text-[10px]">
                      {(selected.rule?.branchPaths || []).map((b, i) => (
                        <Badge key={i} variant="outline" className="font-mono text-[10px] h-5">{b}</Badge>
                      ))}
                      {(selected.rule?.nameContains || []).map((n, i) => (
                        <Badge key={`n${i}`} variant="secondary" className="text-[10px] h-5">name ~ {n}</Badge>
                      ))}
                      {selected.rule?.propertyKey && (
                        <Badge variant="secondary" className="text-[10px] h-5">
                          {selected.rule.propertyKey} = {(selected.rule.propertyValues || []).join(' | ') || 'any'}
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-[10px] h-5">
                        {selected.rule?.leavesOnly ? 'geometry leaves only' : 'includes assemblies'}
                      </Badge>
                    </div>
                  </div>

                  <div className="rounded-md border">
                    <div className="px-2.5 py-1.5 border-b text-[10px] uppercase tracking-wide text-muted-foreground flex items-center">
                      Proposed objects
                      <span className="ml-auto normal-case" data-testid="cognara-object-count">
                        {selected.proposedCount} object(s)
                        {selected.existingObjectCount ? ` · ${selected.existingObjectCount} already linked` : ''}
                      </span>
                    </div>
                    {!selected.proposedCount ? (
                      <div className="p-3 text-xs text-muted-foreground">
                        The rule matched nothing in this model — Cognara could not find geometry for this activity.
                      </div>
                    ) : (
                      <div className="divide-y max-h-[320px] overflow-auto">
                        {selected.proposedObjects.map(o => (
                          <div key={o.dbId} className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-muted/40"
                               data-testid={`cognara-object-${o.dbId}`}>
                            <span className="text-[10px] font-mono text-muted-foreground w-14 shrink-0">#{o.dbId}</span>
                            <span className="text-xs truncate">{o.objectName || '(unnamed)'}</span>
                            <span className="text-[10px] text-muted-foreground truncate ml-auto max-w-[45%]">{o.objectPath}</span>
                            <Button size="icon" variant="ghost" className="h-5 w-5 shrink-0"
                                    onClick={() => viewerRef?.current?.selectAndZoom?.(o.dbId)} title="Zoom to object">
                              <Crosshair className="w-3 h-3" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-5 w-5 shrink-0 text-rose-600"
                                    onClick={() => dropObject(selected, o.dbId)} disabled={!canEdit}
                                    title="Drop from this linkage" data-testid={`cognara-drop-${o.dbId}`}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </ScrollArea>
            </>
          )}
        </ResizablePanel>
      </ResizablePanelGroup>

      {selected && (
        <ObjectScopeEditor
          open={editorOpen}
          onOpenChange={setEditorOpen}
          projectId={projectId}
          modelId={modelId}
          proposal={selected}
          viewerRef={viewerRef}
          onSave={async (dbIds) => {
            const keepStatus = selected.status === 'ACCEPTED' ? 'ACCEPT'
              : selected.status === 'REJECTED' ? 'REJECT' : 'RESET';
            await decide(selected, keepStatus, dbIds);
            toast.success(`Linkage updated · ${dbIds.length} object(s)`);
          }}
        />
      )}

      <AlertDialog open={acceptAllAsk} onOpenChange={setAcceptAllAsk}>
        <AlertDialogContent data-testid="cognara-accept-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Accept all {counts.PENDING || 0} pending proposal(s)?</AlertDialogTitle>
            <AlertDialogDescription data-testid="cognara-accept-all-body">
              Every pending activity is marked accepted regardless of confidence — including the
              low-confidence ones and the activities Cognara flagged as conflicting with their existing
              links. Nothing is written to the 4D model yet; you still press Commit, and you can reject
              or edit any activity afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cognara-accept-all-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-emerald-600 hover:bg-emerald-700"
                               data-testid="cognara-accept-all-confirm"
                               onClick={async (e) => {
                                 e.preventDefault();
                                 setAccepting(true);
                                 await bulkAccept({ all: true });
                                 setAccepting(false);
                               }}>
              {accepting ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              Accept all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!commitAsk} onOpenChange={(o) => { if (!o) setCommitAsk(null); }}>
        <AlertDialogContent data-testid="cognara-commit-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {counts.ACCEPTED
                ? `Commit ${counts.ACCEPTED} accepted linkage(s) into the 4D model?`
                : 'Nothing is accepted yet'}
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="cognara-commit-dialog-body">
              {counts.ACCEPTED ? (
                <>
                  Existing links on those activities are replaced.
                  {commitAsk?.simulate
                    ? ' The timeliner snapshots are rebuilt straight after, so the 4D playback reflects the new links.'
                    : ' Snapshots are left as they are — run the timeliner when you are ready.'}
                  {(counts.PENDING || 0) > 0 && (
                    <> <strong>{counts.PENDING} proposal(s) are still pending</strong> and will not be committed.</>
                  )}
                </>
              ) : (
                <>
                  Commit only writes linkages you have <strong>accepted</strong>. Right now
                  {' '}{counts.PENDING || 0} proposal(s) are pending and 0 are accepted, so there is nothing to write.
                  {eligibleAtThreshold > 0
                    ? ` ${eligibleAtThreshold} of them sit at or above the ${threshold[0]}% confidence threshold — accept those now, or review activities one at a time in the tree.`
                    : ` None of them reach the ${threshold[0]}% confidence threshold — lower the slider, or accept activities individually in the tree.`}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cognara-commit-cancel">Cancel</AlertDialogCancel>
            {!counts.ACCEPTED && eligibleAtThreshold > 0 && (
              <Button size="sm" className="bg-orange-600 hover:bg-orange-700"
                      data-testid="cognara-commit-accept-then"
                      onClick={async () => {
                        const simulate = commitAsk?.simulate;
                        setCommitAsk(null);
                        const n = await bulkAccept();
                        if (n > 0) await runCommit(simulate);
                      }}>
                Accept {eligibleAtThreshold} ≥ {threshold[0]}% and commit
              </Button>
            )}
            {!!counts.ACCEPTED && (
              <AlertDialogAction className="bg-orange-600 hover:bg-orange-700"
                                 data-testid="cognara-commit-confirm"
                                 onClick={() => runCommit(commitAsk?.simulate)}>
                {commitAsk?.simulate ? 'Commit & Simulate' : 'Commit 4D'}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default CognaraLinkHitl;
